import React, { createContext, useContext, useState, useEffect } from 'react';

const CompanyThemeContext = createContext(null);


const defaultTheme = {

  companyName: 'Covenant Tracker',
  logo: null,
  primaryColor: '#2563eb',
  secondaryColor: '#1e293b',


  legalName: '',
  address: '',
  city: '',
  postalCode: '',
  country: 'France',
  siret: '',
  tvaNumber: '',
  phone: '',
  email: '',
  website: '',


  billingEmail: '',
  iban: '',
  bic: '',
  bankName: '',
  currency: 'EUR',
};

export function CompanyThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('companyTheme');
    return saved ? { ...defaultTheme, ...JSON.parse(saved) } : defaultTheme;
  });


  useEffect(() => {
    localStorage.setItem('companyTheme', JSON.stringify(theme));
  }, [theme]);


  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--company-primary', theme.primaryColor);
    root.style.setProperty('--company-secondary', theme.secondaryColor);


    root.style.setProperty('--company-primary-light', adjustColor(theme.primaryColor, 40));
    root.style.setProperty('--company-primary-dark', adjustColor(theme.primaryColor, -20));
    root.style.setProperty('--company-primary-bg', adjustColor(theme.primaryColor, 90));
  }, [theme.primaryColor, theme.secondaryColor]);


  const adjustColor = (color, percent) => {
    const num = parseInt(color.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return '#' + (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    ).toString(16).slice(1);
  };


  const updateTheme = (updates) => {
    setTheme(prev => ({ ...prev, ...updates }));
  };


  const updateBranding = (branding) => {
    updateTheme({
      logo: branding.logo,
      primaryColor: branding.primaryColor,
      secondaryColor: branding.secondaryColor,
    });
  };


  const updateCompanyInfo = (info) => {
    updateTheme({
      companyName: info.name,
      legalName: info.legalName,
      address: info.address,
      city: info.city,
      postalCode: info.postalCode,
      country: info.country,
      siret: info.siret,
      tvaNumber: info.tvaNumber,
      phone: info.phone,
      email: info.email,
      website: info.website,
      billingEmail: info.billingEmail,
      iban: info.iban,
      bic: info.bic,
      bankName: info.bankName,
      currency: info.currency,
      logo: info.logo,
      primaryColor: info.primaryColor,
      secondaryColor: info.secondaryColor,
    });
  };


  const resetTheme = () => {
    setTheme(defaultTheme);
  };


  const getButtonStyle = (variant = 'primary') => {
    if (variant === 'primary') {
      return {
        backgroundColor: theme.primaryColor,
        color: '#ffffff',
      };
    }
    return {
      backgroundColor: 'transparent',
      color: theme.primaryColor,
      borderColor: theme.primaryColor,
    };
  };

  const getAccentStyle = () => ({
    color: theme.primaryColor,
  });

  const getBgAccentStyle = () => ({
    backgroundColor: theme.primaryColor,
  });

  const value = {
    theme,
    updateTheme,
    updateBranding,
    updateCompanyInfo,
    resetTheme,
    getButtonStyle,
    getAccentStyle,
    getBgAccentStyle,
  };

  return (
    <CompanyThemeContext.Provider value={value}>
      {children}
    </CompanyThemeContext.Provider>
  );
}

export function useCompanyTheme() {
  const context = useContext(CompanyThemeContext);
  if (!context) {
    throw new Error('useCompanyTheme must be used within a CompanyThemeProvider');
  }
  return context;
}

export default CompanyThemeContext;